import {
  compose,
  contains,
  identity,
  isEmpty,
  memoizeWith,
  path,
  prop,
} from 'ramda';
import GraphQLJSON from 'graphql-type-json';
import { OwnerAPI, BasicAPI } from '@iris-platform/eks-js-sdk';
import { resolver } from '../util';
import { ApplicationModel } from '../../models';
import config from '../../config';
import { getDomainsForUserId } from '../Domains/resolvers';

const memoize = memoizeWith(identity);

const getCredentialsForDomain = async (domain) => {
  const application = await ApplicationModel().findApplicationByDomain(domain);

  return {
    appkey: path(['fields', 'clientID'], application),
    appsecret: path(['fields', 'clientSecret'], application),
  };
};

const getEksClient = memoize(async (domain) => {
  const credentials = await getCredentialsForDomain(domain);

  return OwnerAPI(
    {
      baseURL: config.authServer,
      ...credentials,
    },
    {
      baseURL: config.eksURL,
      version: 'v1.1',
    },
  );
});

const getBasicEksClient = memoize(token => BasicAPI(
  token,
  {
    baseURL: config.eksURL,
    version: 'v1.2',
  },
));

const isEmptyResponse = compose(
  contains('no keys owned by'),
  prop('message'),
);

const createKeyResolver = resolverFn => resolver.authenticatedResolver.createResolver(
  async (root, args, { user }) => {
    const { domain } = args;
    const userId = prop('user_id', user);
    const availableDomains = await getDomainsForUserId(userId);

    if (!contains(domain, availableDomains)) {
      throw new Error(`User not authorized to access domain ${domain}`);
    }

    const eksClient = await getEksClient(domain);

    try {
      return await resolverFn(eksClient, args);
    } catch (error) {
      if (isEmptyResponse(error)) return [];
      if (error.message.includes('owner has no secrets')) return [];
      throw error;
    }
  },
);

const createBasicAuthKeyResolver = resolverFn => resolver.authenticatedResolver.createResolver(
  async (root, args) => {
    const { token } = args;

    const eksClient = await getBasicEksClient(token);

    try {
      return await resolverFn(eksClient, args);
    } catch (error) {
      if (isEmptyResponse(error)) return [];
      if (error.message.includes('owner has no secrets')) return [];
      throw error;
    }
  },
);

const setKey = createKeyResolver(
  async (eksClient, {
    key, data, readers = [], writers = [],
  }) => {
    await eksClient.createSecret(key, data);

    if (!isEmpty(readers) || !isEmpty(writers)) {
      await eksClient.addAccess(key, readers, writers);
    }

    return true;
  },
);

const addKeyAccess = createKeyResolver(
  async (eksClient, { key, readers = [], writers = [] }) => {
    await eksClient.addAccess(key, readers, writers);

    return true;
  },
);

const removeKeyAccess = createKeyResolver(
  async (eksClient, { key, readers = [], writers = [] }) => {
    await eksClient.removeAccess(key, readers, writers);

    return true;
  },
);

const deleteKey = createKeyResolver(async (eksClient, { key }) => {
  await eksClient.deleteSecret(key);

  return true;
});

const getKey = createKeyResolver(async (eksClient, { key }) => {
  const data = await eksClient.getSecretValue(key);
  const { readers, writers } = await eksClient.getSecretAccess(key);

  return {
    key,
    data,
    readers,
    writers,
  };
});

const getKeyData = createKeyResolver(async (eksClient, { key }) => eksClient.getSecretValue(key));

const getKeyAccess = createKeyResolver(async (eksClient, { key }) => {
  const access = await eksClient.getSecretAccess(key);

  return {
    key,
    ...access,
  };
});

const getKeys = createKeyResolver(async eksClient => eksClient.getAllKeys());

const getBasicAuthKey = createBasicAuthKeyResolver(async (basicEksClient, { key }) => {
  const data = await basicEksClient.basicGetSecretValue(key);

  return {
    key,
    data,
  };
});

const getBasicAuthKeyData = createBasicAuthKeyResolver(
  async (basicEksClient, { key }) => basicEksClient.getSecretValue(key),
);

const getBasicAuthKeys = createBasicAuthKeyResolver(
  async basicEksClient => basicEksClient.basicGetAllKeys(),
);

const setBasicAuthKey = createBasicAuthKeyResolver(
  async (basicEksClient, { key, data }) => {
    await basicEksClient.basicCreateSecret(key, data);

    return true;
  },
);

const deleteBasicAuthKey = createBasicAuthKeyResolver(async (basicEksClient, { key }) => {
  await basicEksClient.basicDeleteSecret(key);

  return true;
});

const generateBasicAuthToken = createBasicAuthKeyResolver(
  async basicEksClient => basicEksClient.basicGetToken(),
);

export default {
  Mutation: {
    setKey,
    addKeyAccess,
    removeKeyAccess,
    deleteKey,
    setBasicAuthKey,
    deleteBasicAuthKey,
  },
  Query: {
    getKey,
    getKeyData,
    getKeyAccess,
    getKeys,
    getBasicAuthKey,
    getBasicAuthKeyData,
    getBasicAuthKeys,
    generateBasicAuthToken,
  },
  JSON: GraphQLJSON,
};
